import { useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw, Eye, Save, Bold, Italic } from 'lucide-react';
import Button from '@/components/ui/Button';
import { useAuthStore } from '@/context/authStore';
import { useUpdateTenantProfile } from '@/hooks/useTenant';
import { tenantsService, ReminderTemplateKind } from '@/services/tenants';
import { renderTemplate, PREVIEW_VALUES } from '@/utils/reminderTemplates';
import toast from 'react-hot-toast';

// Emojis a mano, pensados para los mensajes de una veterinaria
const EMOJIS = ['🐾', '🐶', '🐱', '💉', '🩺', '✂️', '📅', '⏰', '📍', '❤️', '✅', '😊'];

/**
 * Muestra el texto como lo va a ver el cliente en WhatsApp: *negrita* y _cursiva_
 * aplicados de verdad, en vez de los asteriscos y guiones bajos crudos.
 */
/**
 * Vista previa con las reglas REALES de WhatsApp, que son mas estrictas de lo que
 * parece: el asterisco tiene que tocar el texto y el formato no cruza renglones. Un
 * "* hola *" en WhatsApp se ve con los asteriscos, no en negrita.
 *
 * Antes la vista previa aceptaba los espacios y mostraba negrita igual, asi que el
 * mensaje se veia bien en el editor y salia con los asteriscos en el celular.
 */
const FORMATO_WHATSAPP = /(\*[^\s*](?:[^*\n]*[^\s*])?\*|_[^\s_](?:[^_\n]*[^\s_])?_)/g;

function formatWhatsApp(text: string) {
  return text.split(FORMATO_WHATSAPP).map((part, i) => {
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <strong key={i}>{part.slice(1, -1)}</strong>;
    }
    if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return <span key={i}>{part}</span>;
  });
}

const TABS: { kind: ReminderTemplateKind; label: string; field: string }[] = [
  { kind: 'visit', label: 'Control', field: 'reminder_template_visit' },
  { kind: 'vaccine', label: 'Vacuna', field: 'reminder_template_vaccine' },
  { kind: 'deworming', label: 'Desparasitación', field: 'reminder_template_deworming' },
  { kind: 'grooming', label: 'Peluquería', field: 'reminder_template_grooming' },
  { kind: 'appointment', label: 'Turno médico', field: 'reminder_template_appointment' },
];

/**
 * Edición de los mensajes de WhatsApp de los recordatorios.
 *
 * Cada veterinaria guarda sus propios textos; si un campo queda vacío se usa el mensaje
 * por defecto del sistema. Las variables van entre llaves y se reemplazan al enviar.
 */
export default function ReminderTemplatesModal({ onClose }: { onClose: () => void }) {
  const tenant = useAuthStore((s) => s.tenant);
  const updateProfile = useUpdateTenantProfile();

  const { data: config } = useQuery({
    queryKey: ['tenant', 'reminder-template-defaults'],
    queryFn: tenantsService.getReminderTemplateDefaults,
    staleTime: 60 * 60_000,
  });

  const [activeKind, setActiveKind] = useState<ReminderTemplateKind>('visit');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>(() => ({
    reminder_template_visit: tenant?.reminder_template_visit || '',
    reminder_template_vaccine: tenant?.reminder_template_vaccine || '',
    reminder_template_deworming: tenant?.reminder_template_deworming || '',
    reminder_template_grooming: tenant?.reminder_template_grooming || '',
    reminder_template_appointment: tenant?.reminder_template_appointment || '',
  }));

  const active = TABS.find((t) => t.kind === activeKind)!;
  const defaultText = config?.defaults?.[activeKind] ?? '';
  const variables = config?.variables?.[activeKind] ?? [];

  // Lo que se manda es lo cargado a mano; si está vacío, el texto por defecto
  const currentText = drafts[active.field] || '';
  const effectiveText = currentText.trim() || defaultText;

  const preview = useMemo(() => renderTemplate(effectiveText, PREVIEW_VALUES), [effectiveText]);

  // Un marcador separado del texto por un espacio ("* hola *") no es formato para
  // WhatsApp: llega con los asteriscos a la vista. Se avisa en vez de reescribir el
  // texto solo, porque tocarlo con regex rompe los mensajes que ya estan bien.
  //
  // Los marcadores se emparejan de a dos por renglon, igual que hace WhatsApp. Buscarlos
  // con un regex suelto da falsos positivos: engancha el cierre de una negrita con la
  // apertura de la siguiente y marca como rota una plantilla que esta perfecta.
  const tieneMarcadoresSueltos = useMemo(() => {
    const sueltos = (linea: string, marcador: string) => {
      const pos: number[] = [];
      for (let i = 0; i < linea.length; i++) if (linea[i] === marcador) pos.push(i);
      for (let i = 0; i + 1 < pos.length; i += 2) {
        const abre = pos[i];
        const cierra = pos[i + 1];
        if (cierra === abre + 1) continue; // marcador vacio, no es formato
        if (/\s/.test(linea[abre + 1]) || /\s/.test(linea[cierra - 1])) return true;
      }
      return false;
    };
    return effectiveText
      .split('\n')
      .some((linea) => sueltos(linea, '*') || sueltos(linea, '_'));
  }, [effectiveText]);

  const setText = (value: string) => setDrafts((d) => ({ ...d, [active.field]: value }));

  /**
   * Inserta texto DONDE ESTA EL CURSOR (antes se pegaba todo al final, que para alguien
   * que no es tecnico es imposible de usar). Si el mensaje todavia es el de por defecto,
   * primero lo copia al editor para poder trabajar sobre el.
   */
  const insertAtCursor = (text: string, wrapSelection = false) => {
    const el = textareaRef.current;
    const base = currentText || defaultText;

    if (!el) {
      setText(base + text);
      return;
    }

    const start = el.selectionStart ?? base.length;
    const end = el.selectionEnd ?? base.length;
    const selected = base.slice(start, end);

    // WhatsApp solo pone negrita si el marcador toca el texto. Al seleccionar una
    // palabra casi siempre se arrastra un espacio, asi que los espacios de los bordes
    // se dejan afuera: "* hola *" no es negrita, " *hola* " si.
    let inserted = text;
    if (wrapSelection && selected) {
      const [, pre, core, post] = selected.match(/^(\s*)([\s\S]*?)(\s*)$/) as RegExpMatchArray;
      inserted = core ? `${pre}${text}${core}${text}${post}` : selected;
    }
    const next = base.slice(0, start) + inserted + base.slice(end);
    setText(next);

    // Dejar el cursor después de lo insertado
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + inserted.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSave = async () => {
    try {
      // Vacío = volver al texto por defecto del sistema
      await updateProfile.mutateAsync({
        reminder_template_visit: drafts.reminder_template_visit.trim(),
        reminder_template_vaccine: drafts.reminder_template_vaccine.trim(),
        reminder_template_deworming: drafts.reminder_template_deworming.trim(),
        reminder_template_grooming: drafts.reminder_template_grooming.trim(),
        reminder_template_appointment: drafts.reminder_template_appointment.trim(),
      });
      toast.success('Mensajes guardados');
      onClose();
    } catch {
      /* el hook ya muestra el error */
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Estos son los mensajes que se mandan por WhatsApp. Las palabras entre llaves se
        reemplazan solas con los datos de cada recordatorio.
      </p>

      {/* Tipo de recordatorio */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.kind}
            type="button"
            onClick={() => setActiveKind(t.kind)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition-colors ${
              activeKind === t.kind
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            {t.label}
            {!drafts[t.field].trim() && <span className="text-xs text-gray-400"> · por defecto</span>}
          </button>
        ))}
      </div>

      {/* Variables disponibles */}
      {variables.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-500">Datos:</span>
          {variables.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => insertAtCursor(`{${v}}`)}
              title="Insertar donde está el cursor"
              className="text-xs font-mono px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              {`{${v}}`}
            </button>
          ))}
        </div>
      )}

      {/* Emojis y formato */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-gray-500">Iconos:</span>
        {EMOJIS.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => insertAtCursor(e)}
            title="Insertar donde está el cursor"
            className="text-base leading-none px-1.5 py-1 rounded-lg hover:bg-gray-100"
          >
            {e}
          </button>
        ))}
        <span className="w-px h-5 bg-gray-200 mx-1" />
        <button
          type="button"
          onClick={() => insertAtCursor('*', true)}
          title="Negrita: seleccioná el texto y tocá acá"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
        >
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => insertAtCursor('_', true)}
          title="Cursiva: seleccioná el texto y tocá acá"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
        >
          <Italic className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Editor */}
      <textarea
        ref={textareaRef}
        value={currentText}
        onChange={(e) => setText(e.target.value)}
        rows={9}
        placeholder={defaultText}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono resize-y focus:outline-none focus:border-green-400"
      />
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-400">
          {currentText.trim()
            ? 'Se usa este texto.'
            : 'Vacío: se usa el mensaje por defecto que ves en gris.'}
        </p>
        {currentText.trim() && (
          <button
            type="button"
            onClick={() => setText('')}
            className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            <RotateCcw className="h-3 w-3" />
            Restaurar el original
          </button>
        )}
      </div>

      {/* Vista previa */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase mb-1.5 flex items-center gap-1.5">
          <Eye className="h-3.5 w-3.5" />
          Así lo va a recibir el cliente
        </p>
        <div className="bg-green-50 border border-green-200 rounded-xl p-3">
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{formatWhatsApp(preview)}</p>
        </div>
        {tieneMarcadoresSueltos && (
          <p className="text-xs text-amber-700 mt-1.5">
            Hay un <code>*</code> o <code>_</code> separado del texto por un espacio. WhatsApp
            no lo toma como formato y lo va a mostrar tal cual. Seleccioná la palabra y tocá el
            botón de negrita para acomodarlo.
          </p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-1">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={handleSave} loading={updateProfile.isPending}>
          <Save className="h-4 w-4 mr-2" />
          Guardar mensajes
        </Button>
      </div>
    </div>
  );
}
